import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useApolloClient, useMutation } from '@apollo/client';
import AgentPortalError from 'errors/AgentPortalError';
import { ACTION_CODES } from 'constants/errors';
import { withRouter } from 'react-router';
import { injectIntl } from 'react-intl';

import { ADD_ADDITIONAL_DETAILS, ADD_CUSTOMER_PASSENGERS, UPDATE_ADDITIONAL_DETAILS, UPDATE_CUSTOMER_PASSENGERS } from 'store/mutations';
import { bookingShape } from 'constants/propTypes';
import { DEFAULT_LANGUAGE, LANGUAGE_KEY } from 'constants/booking';
import SeatSelection from 'components/shared/SeatSelection';

import GroupDetails from './GroupDetails';
import updateBookingLanguage from 'utils/updateBookingLanguage';
import { nonCancelledEurostarAndGreenspeedLegs } from 'utils/booking';

import { getMinorsAmount, getFirstNonCancelledSegmentIsoDate, setMinors } from 'utils/minors';

const Index = (props) => {
  const {
    bookingData: { customer, additionalDetails, outbound, inbound, reference },
    bookingData,
    history: { push, location },
    canAddSpecialAssistanceToBooking
  } = props;
  const defaultLanguage = additionalDetails?.find(({ key }) => key === LANGUAGE_KEY)?.value || DEFAULT_LANGUAGE;
  const [bookingLanguage, setBookingLanguage] = useState(defaultLanguage);
  const [isAssistanceSelected, setIsAssistanceSelected] = useState(false);
  const apolloClient = useApolloClient();

  const { companyName, email, unformattedPhoneNumber } = customer || {};
  const membershipNumber = new URLSearchParams(location.search).get('membershipNumber');
  const membershipNumberUrl = membershipNumber ? `?membershipNumber=${membershipNumber}` : '';
  const mutation = companyName && email && unformattedPhoneNumber?.number ? UPDATE_CUSTOMER_PASSENGERS : ADD_CUSTOMER_PASSENGERS;

  const [addAdditionalDetails, { loading: addAdditionalDetailsLoading }] = useMutation(ADD_ADDITIONAL_DETAILS, {
    refetchQueries: ['booking']
  });
  const [updateAdditionalDetails, { loading: updateAdditionalDetailsLoading }] = useMutation(UPDATE_ADDITIONAL_DETAILS, {
    refetchQueries: ['booking']
  });

  const minorsAmount = getMinorsAmount(additionalDetails);
  const minorOutbound = getFirstNonCancelledSegmentIsoDate(outbound);
  const minorInbound = getFirstNonCancelledSegmentIsoDate(inbound);

  const updateMinors = (minors) =>
    setMinors({
      addAdditionalDetails,
      bookingReference: reference,
      minors: minors.toString(),
      minorsAmount,
      minorOutbound,
      minorInbound,
      updateAdditionalDetails
    });

  const [updatePassenger, { loading, error, client }] = useMutation(mutation, {
    onCompleted: async ({ updatePassengers: { reference } }) => {
      await updateBookingLanguage(apolloClient, additionalDetails, reference, bookingLanguage);

      if (isAssistanceSelected) {
        push(`/booking/${reference}/pax/assistance${membershipNumberUrl}`);
      } else {
        push(`/booking/${reference}/review-booking${membershipNumberUrl}`);
      }
    }
  });

  if (error) {
    throw new AgentPortalError(error, ACTION_CODES.DEFAULT_ACTION);
  }

  return (
    <SeatSelection
      outbound={nonCancelledEurostarAndGreenspeedLegs(bookingData.outbound)}
      inbound={nonCancelledEurostarAndGreenspeedLegs(bookingData.inbound)}
      bookingData={bookingData}
      isNewBooking
    >
      {({
        renderedSeatSelection,
        selectedSeats,
        selectedPassenger,
        selectedPassengers,
        disabledContinue,
        actions: { updatePassengerSeats, onSelectNewPassenger, onRemovePassengerSelection }
      }) => (
        <>
          {renderedSeatSelection}
          <GroupDetails
            {...props}
            {...{
              selectedSeats,
              selectedPassenger,
              selectedPassengers,
              updatePassengerSeats,
              onSelectNewPassenger,
              onRemovePassengerSelection
            }}
            client={client}
            membershipNumber={membershipNumber}
            updatePassenger={updatePassenger}
            updateMinors={updateMinors}
            loading={loading || addAdditionalDetailsLoading || updateAdditionalDetailsLoading}
            disabledContinue={disabledContinue}
            bookingLanguage={bookingLanguage}
            setBookingLanguage={setBookingLanguage}
            canAddSpecialAssistanceToBooking={canAddSpecialAssistanceToBooking}
            isAssistanceSelected={isAssistanceSelected}
            setIsAssistanceSelected={setIsAssistanceSelected}
          />
        </>
      )}
    </SeatSelection>
  );
};

Index.propTypes = {
  bookingData: bookingShape,
  history: PropTypes.shape({
    push: PropTypes.func,
    location: PropTypes.shape({
      search: PropTypes.string
    })
  }),
  canAddSpecialAssistanceToBooking: PropTypes.bool.isRequired
};

export default injectIntl(withRouter(Index));
